import { union } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { PlateGripSummary, PlateMaterialProperties, PlateTypeProperties } from '@paragon/contract-library';

export interface MutablePlatePropertiesStore {
  plateMaterialPropertySetRequests: { [getPlateTypeStringResult: string]: string };
  plateMaterialPropertySets: { [getPlateTypeStringResult: string]: PlateMaterialProperties };

  plateTypePropertiesRequests: { [getPlateTypeStringResult: string]: string };
  plateTypeProperties: { [getPlateTypeStringResult: string]: PlateTypeProperties | undefined };

  plateTypeLateralResistanceValuesRequests: { [lateralResistanceValuesKey: string]: string };
  notFoundLateralResistanceValues: string[];
  plateTypeLateralResistanceValues: { [lateralResistanceValuesKey: string]: PlateGripSummary };
}

export type PlateProperties = Readonly<MutablePlatePropertiesStore>;

export function shouldMakePlateTypeLateralResistanceValuesRequest(
  state: PlateProperties,
  lateralResistanceValuesKey: string,
) {
  return !(
    state.plateTypeLateralResistanceValuesRequests[lateralResistanceValuesKey] ||
    state.notFoundLateralResistanceValues.includes(lateralResistanceValuesKey)
  );
}

const initialState: PlateProperties = {
  plateMaterialPropertySetRequests: {},
  plateMaterialPropertySets: {},

  plateTypePropertiesRequests: {},
  plateTypeProperties: {},

  plateTypeLateralResistanceValuesRequests: {},
  notFoundLateralResistanceValues: [],
  plateTypeLateralResistanceValues: {},
};

const platePropertiesSlice = createSlice({
  name: 'plateProperties',
  initialState,
  reducers: {
    setPlateMaterialPropertySetRequested: (
      state,
      action: PayloadAction<{ readonly getPlateTypeStringResult: string }>,
    ) => {
      state.plateMaterialPropertySetRequests[action.payload.getPlateTypeStringResult] =
        action.payload.getPlateTypeStringResult;
    },
    setPlateMaterialPropertySetForPlateType: (
      state,
      action: PayloadAction<{
        readonly getPlateTypeStringResult: string;
        readonly propertySet: PlateMaterialProperties;
      }>,
    ) => {
      state.plateMaterialPropertySets[action.payload.getPlateTypeStringResult] = action.payload.propertySet;
    },
    setPlateTypePropertiesRequested: (state, action: PayloadAction<{ readonly getPlateTypeStringResult: string }>) => {
      state.plateTypePropertiesRequests[action.payload.getPlateTypeStringResult] =
        action.payload.getPlateTypeStringResult;
    },
    setPlateTypeProperties: (
      state,
      action: PayloadAction<{
        readonly plateTypeProperties: PlateTypeProperties;
        readonly getPlateTypeStringResult: string;
      }>,
    ) => {
      state.plateTypeProperties[action.payload.getPlateTypeStringResult] = action.payload.plateTypeProperties;
    },
    setPlateLateralResistanceValuesRequested: (state, action: PayloadAction<string>) => {
      state.plateTypeLateralResistanceValuesRequests[action.payload] = action.payload;
    },
    setPlateLateralResistanceValues: (
      state,
      action: PayloadAction<{ key: string; lateralResistanceSet: PlateGripSummary }>,
    ) => {
      state.plateTypeLateralResistanceValues[action.payload.key] = castDraft(action.payload.lateralResistanceSet);
    },
    setPlateLateralResistanceValuesRequestedFailed: (state, action: PayloadAction<string>) => {
      delete state.plateTypeLateralResistanceValuesRequests[action.payload];
      state.notFoundLateralResistanceValues = union(state.notFoundLateralResistanceValues, [action.payload]);
    },
  },
});

export const {
  setPlateMaterialPropertySetRequested,
  setPlateMaterialPropertySetForPlateType,
  setPlateTypePropertiesRequested,
  setPlateTypeProperties,
  setPlateLateralResistanceValuesRequested,
  setPlateLateralResistanceValues,
  setPlateLateralResistanceValuesRequestedFailed,
} = platePropertiesSlice.actions;
export const plateProperties = platePropertiesSlice.reducer;
